import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const ease = [0.22, 1, 0.36, 1];

export default function CollectionPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/collections/${slug}`).then((r) => setData(r.data)).catch(() => nav("/shop"));
  }, [slug, nav]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Caricamento…</div>;

  const { collection, products, subgroups } = data;
  const ungrouped = products.filter((p) => !(p.subgroup || "").trim());

  const renderGrid = (items) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
      {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
    </div>
  );

  return (
    <div className="relative z-10 pt-28 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10" data-testid="back-to-collections">
        <ArrowLeft size={16} /> Tutte le collezioni
      </Link>

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="mb-16">
        <p className="text-sm uppercase tracking-[0.3em] text-gold mb-4">Collezione</p>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-none mb-5" data-testid="collection-title">{collection.name}</h1>
        {collection.description && <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">{collection.description}</p>}
      </motion.div>

      {subgroups.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-14" data-testid="subgroup-filters">
          {subgroups.map((sg) => (
            <button
              key={sg}
              onClick={() => document.getElementById(`sg-${sg}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="rounded-full border border-border px-5 py-2 text-sm hover:bg-gold hover:text-white hover:border-gold transition-colors"
              data-testid={`subgroup-filter-${sg}`}
            >
              {sg}
            </button>
          ))}
          {ungrouped.length > 0 && (
            <button
              onClick={() => document.getElementById("sg-altri")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="rounded-full border border-border px-5 py-2 text-sm hover:bg-gold hover:text-white hover:border-gold transition-colors"
              data-testid="subgroup-filter-altri"
            >
              Altri incanti
            </button>
          )}
        </div>
      )}

      {products.length === 0 && <p className="text-muted-foreground">Nessun articolo in questa collezione, per ora.</p>}

      {/* Grouped by subgroup */}
      {subgroups.map((sg) => (
        <section key={sg} id={`sg-${sg}`} className="mb-20 scroll-mt-28" data-testid={`subgroup-${sg}`}>
          <div className="flex items-center gap-4 mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-light">{sg}</h2>
            <div className="flex-1 h-px bg-border" />
          </div>
          {renderGrid(products.filter((p) => (p.subgroup || "").trim() === sg))}
        </section>
      ))}

      {/* Ungrouped products */}
      {ungrouped.length > 0 && (
        <section className="mb-10 scroll-mt-28" id="sg-altri">
          {subgroups.length > 0 && (
            <div className="flex items-center gap-4 mb-8">
              <h2 className="font-serif text-3xl md:text-4xl font-light">Altri incanti</h2>
              <div className="flex-1 h-px bg-border" />
            </div>
          )}
          {renderGrid(ungrouped)}
        </section>
      )}

      {(collection.gallery || []).length > 0 && (
        <section className="mt-24 mb-10 border-t border-border pt-16" data-testid="collection-gallery">
          <div className="flex items-center gap-4 mb-8">
            <h2 className="font-serif text-3xl md:text-4xl font-light">Abbinamenti</h2>
            <div className="flex-1 h-px bg-border" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {collection.gallery.map((g, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease }}
                data-testid={`gallery-item-${i}`}
              >
                <div className="overflow-hidden rounded-md bg-muted aspect-[4/5] mb-4">
                  <img src={g.image} alt={g.caption || "Abbinamento"} className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105" />
                </div>
                {g.caption && <figcaption className="text-sm text-muted-foreground leading-relaxed">{g.caption}</figcaption>}
              </motion.figure>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
